import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

// DATA
import { accountsSelectors } from 'app-components/flux/accounts';
import { categoriesSelectors } from 'app-components/flux/categories';
import { rootUiActions } from 'app-components/flux/root-ui';
import { scheduledTransactionsSelectors, scheduledTransactionsTypes } from 'app-components/flux/scheduled-transactions';
import type {
  ScheduledTransaction,
  ScheduledTransactionType,
} from 'app-components/flux/scheduled-transactions/scheduledTransactionsTypes';
import { Transaction } from 'app-components/flux/transactions/transactionsTypes';
import type { TransactionProps } from 'app-components/flux/transactions/transactionsTypes';
import { tracker } from 'app-components/utils/core';

// CUSTOM
import ScheduledTransactionEditDialog from 'components/Dialogs/ScheduledTransactionEditDialog';

import {
  getRootModalRemoveDialogOptions,
  type RootModalDrawerOptions,
} from 'components/Drawers/standardDrawerContract';
import SelectSeriesTransactionDialog from 'components/Series/SelectSeriesTransactionDialog';
import { useAppSelector } from 'hooks/redux';
import { useCreateBillScoutSchedule } from 'hooks/useCreateBillScoutSchedule';
import ReviewDialog from './ReviewDialog';
import { DIALOG_TYPE } from './actions';

const typeEnums = scheduledTransactionsTypes.STTypeEnum;

// ### - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - ###
// ### - - -                  Dialog Component                   - - - ###
// ### - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - ###
type ScheduledTransactionAddDialogProps = {
  confirmation?: boolean;
  dialogType?: scheduledTransactionsTypes.STType;
  rootModalDrawer?: RootModalDrawerOptions;
};

function ScheduledTransactionAddDialog(props: ScheduledTransactionAddDialogProps): React.JSX.Element {
  const { confirmation = true, dialogType, rootModalDrawer } = props;
  const dispatch = useDispatch();
  const models = useAppSelector(scheduledTransactionsSelectors.getScheduledTransactions);
  const { create: createBillScoutSchedule } = useCreateBillScoutSchedule();

  const [open, setOpen] = useState(true);

  const [manualClient, setManualClient] = useState<string | undefined>();
  const [waitingClient, setWaitingClient] = useState<string | undefined>();
  const [createdEdit, setCreatedEdit] = useState<boolean | undefined>();
  const [editTab, setEditTab] = useState(0);
  const accounts = useAppSelector(accountsSelectors.getTransactionAccounts);
  const accountIds = useMemo(() => accounts.keySeq(), [accounts]);
  const incomeCategory = useAppSelector(categoriesSelectors.getPersonalIncomeCategory);

  let createdModel: ScheduledTransaction | undefined;
  const finalClientId = waitingClient || manualClient;
  if (finalClientId) {
    createdModel = models.find((model) => model.clientId === finalClientId);
  }

  const defaultModel: ScheduledTransactionType = {
    clientId: manualClient,
    transaction: new (Transaction({
      isBill: dialogType === typeEnums.BILL,
      isSubscription: dialogType === typeEnums.SUBSCRIPTION,
      ...(dialogType === typeEnums.INCOME && incomeCategory?.id
        ? { coa: { id: incomeCategory.id, type: 'CATEGORY' } }
        : {}),
    }))(),
  };

  const closeDialog = (): void => {
    if (!rootModalDrawer) {
      setOpen(false);
    }

    dispatch(rootUiActions.removeDialog(DIALOG_TYPE, getRootModalRemoveDialogOptions(rootModalDrawer)));
  };

  const selectTransaction = (transaction?: TransactionProps): void => {
    if (transaction?.id) {
      const clientId = createBillScoutSchedule(transaction.id, {
        isSubscription: dialogType === typeEnums.SUBSCRIPTION,
        sourceIsExcludedFromF2S:
          typeof transaction.isExcludedFromF2S === 'boolean' ? transaction.isExcludedFromF2S : undefined,
        sourceIsExcludedFromReports:
          typeof transaction.isExcludedFromReports === 'boolean' ? transaction.isExcludedFromReports : undefined,
        sourceBusinessId: transaction.businessId ?? undefined,
      });
      setWaitingClient(clientId);
      if (!confirmation) {
        closeDialog();
        // returnFunc?.(clientId);
      }
    } else {
      setManualClient(uuidv4().toUpperCase());
    }
    // eslint-disable-next-line no-nested-ternary
    const type =
      dialogType === typeEnums.BILL
        ? 'bills'
        : dialogType === typeEnums.INCOME
          ? 'income'
          : dialogType === typeEnums.SUBSCRIPTION
            ? 'subscriptions'
            : dialogType === typeEnums.TRANSFER
              ? 'transfers'
              : undefined;
    if (type) {
      tracker.track(tracker.events.editSpendingPlanBucket, { location: type, field: 'add' });
    }
  };

  const createSubmit = (): void => {
    setWaitingClient(waitingClient || manualClient);
    setCreatedEdit(false);
  };

  const goBack = (): void => {
    if (createdEdit) {
      setCreatedEdit(false);
    } else {
      setManualClient(undefined);
    }
  };

  const edit = (initialTab = 0): void => {
    setEditTab(initialTab);
    setCreatedEdit(true);
  };

  let isBill: boolean | undefined;
  let isTransfer = false;
  let title: string | undefined;
  if (dialogType === typeEnums.BILL) {
    isBill = true;
    title = 'Add bill';
  } else if (dialogType === typeEnums.SUBSCRIPTION) {
    isBill = true;
    title = 'Add subscription';
  } else if (dialogType === typeEnums.INCOME) {
    isBill = false;
    title = 'Add income';
  } else if (dialogType === typeEnums.TRANSFER) {
    isTransfer = true;
    isBill = true;
    title = 'Add transfer or credit card payment';
  }

  return (
    <>
      {open && !manualClient && !waitingClient && (
        <SelectSeriesTransactionDialog
          open
          onClose={closeDialog}
          title={title || 'Add a Bill or Paycheck'}
          selectorProps={{ isBill, isTransfer, accountIds }}
          onTransactionSelected={selectTransaction}
          subtitle='Choose a transaction to make recurring'
          rootModalDrawer={rootModalDrawer}
        />
      )}

      {open && (createdEdit || (!createdModel && Boolean(manualClient))) && (
        <ScheduledTransactionEditDialog
          open
          scheduledTransaction={createdModel || defaultModel}
          onSubmit={createSubmit}
          onClose={closeDialog}
          onBack={goBack}
          title={createdEdit ? 'Edit Series' : 'Create Series'}
          subscriptionInitial={dialogType === typeEnums.SUBSCRIPTION}
          incomeInitial={dialogType === typeEnums.INCOME}
          transferInitial={dialogType === typeEnums.TRANSFER}
          initialTab={editTab}
          rootModalDrawer={rootModalDrawer}
        />
      )}

      {open && !createdEdit && Boolean(waitingClient || createdModel) && (
        <ReviewDialog open model={createdModel} onClose={closeDialog} edit={edit} rootModalDrawer={rootModalDrawer} />
      )}
    </>
  );
}

export default ScheduledTransactionAddDialog;
